import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Loader2, Pencil, Plus, Trash2, X } from 'lucide-react'
import {
  deleteModelPricing, getCreditConversion, setCreditsPerUsd,
  updateRuntimePricing, upsertModelPricing,
  type CreditConversion, type ModelPricingRow,
} from '@/api/billingAdmin'
import { Field, InfoBox, Td, Th, errText, tableStyle } from '../adminUi'

const PROVIDERS = ['', 'anthropic', 'openai', 'google', 'deepseek', 'claude_code', 'codex']

/**
 * Credit conversion — the whole story of how AI use becomes credits, in one
 * place, with exactly two dials:
 *
 *   1. The model's USD price per million tokens (the table below — this is
 *      where the dollar value of a call comes from, and per-model differences
 *      live HERE and only here).
 *   2. ONE global credits-per-USD ratio.
 *
 * Credits pay for LLM usage only. Runtime seats, downloads and registration
 * fees are priced in money (INR/USD) on the Plans / project Team panels; the
 * platform seat defaults live at the bottom of this panel.
 */
export default function PricingCurrencyPanel() {
  const [data, setData] = useState<CreditConversion | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingSeats, setSavingSeats] = useState(false)
  const [runtime, setRuntime] = useState<Record<string, number>>({})
  const [editing, setEditing] = useState<Partial<ModelPricingRow> | null>(null)

  const load = useCallback(async () => {
    try {
      const next = await getCreditConversion()
      setData(next)
      setRuntime(next.runtime_pricing)
    } catch (e) {
      toast.error(errText(e, 'Could not load credit conversion settings'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const saveRatio = async (value: number) => {
    try {
      setData(await setCreditsPerUsd(value))
      toast.success('Conversion ratio saved')
    } catch (e) {
      toast.error(errText(e, 'Could not save the ratio'))
    }
  }

  const saveModelRow = async () => {
    if (!editing?.model_prefix?.trim()) { toast.error('Enter a model prefix'); return }
    try {
      setData(await upsertModelPricing({
        model_prefix: editing.model_prefix.trim(),
        provider: editing.provider || null,
        input_usd_per_million: editing.input_usd_per_million ?? 0,
        output_usd_per_million: editing.output_usd_per_million ?? 0,
        is_active: editing.is_active ?? true,
      }))
      setEditing(null)
      toast.success('Model pricing saved')
    } catch (e) {
      toast.error(errText(e, 'Could not save the model pricing'))
    }
  }

  const saveSeats = async () => {
    setSavingSeats(true)
    try {
      setRuntime(await updateRuntimePricing({
        default_runtime_user_cost_usd_minor: runtime.default_runtime_user_cost_usd_minor,
        default_runtime_user_cost_inr_minor: runtime.default_runtime_user_cost_inr_minor,
        default_pro_invite_cost_usd_minor: runtime.default_pro_invite_cost_usd_minor,
        default_pro_invite_cost_inr_minor: runtime.default_pro_invite_cost_inr_minor,
      }))
      toast.success('Seat pricing saved')
    } catch (e) {
      toast.error(errText(e, 'Could not save seat pricing'))
    } finally {
      setSavingSeats(false)
    }
  }

  if (loading || !data) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spin" /></div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <InfoBox>
        <strong>Credits pay for one thing: AI usage.</strong> A call's cost is
        the model's USD price per token (the table below) times the tokens it
        used, and that dollar cost burns credits through the single ratio here.
        There is no per-model credit ratio — a model's price difference already
        lives in its USD price. Everything else (runtime seats, downloads,
        registration fees) is charged in INR/USD, never credits.
      </InfoBox>

      {/* ── The one ratio ── */}
      <div>
        <div style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          Credits per USD of AI cost
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', maxWidth: 400 }}>
          <Field label="Credits per USD"
                 hint={`At ${data.credits_per_usd}, one credit is worth $${(1 / (data.credits_per_usd || 100)).toFixed(4)} of AI cost.`}>
            <input
              type="number" min={1} step="1"
              defaultValue={data.credits_per_usd}
              onBlur={(e) => {
                const value = parseFloat(e.target.value)
                if (value > 0 && value !== data.credits_per_usd) void saveRatio(value)
              }}
              style={{ width: '100%' }}
            />
          </Field>
        </div>
      </div>

      {/* ── Model USD pricing (view/edit) ── */}
      <div>
        <div style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.25rem' }}>
          Model pricing (USD per 1M tokens)
        </div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
          The table the dollar value of every call is calculated from. Matching is
          by longest model-id prefix; a model with no row falls back to the built-in
          defaults.
        </div>
        <table style={{ ...tableStyle, marginTop: 0 }}>
          <thead>
            <tr>
              <Th>Provider</Th><Th>Model prefix</Th>
              <Th right>$ / 1M input</Th><Th right>$ / 1M output</Th>
              <Th>Active</Th><Th />
            </tr>
          </thead>
          <tbody>
            {data.model_pricing.map((r) => (
              <tr key={r.id} style={r.is_active ? undefined : { opacity: 0.5 }}>
                <Td>{r.provider || <span style={{ color: 'var(--color-text-secondary)' }}>any</span>}</Td>
                <Td style={{ fontFamily: 'monospace' }}>{r.model_prefix}</Td>
                <Td right>${r.input_usd_per_million}</Td>
                <Td right>${r.output_usd_per_million}</Td>
                <Td>{r.is_active ? 'Yes' : 'No'}</Td>
                <Td right>
                  <span style={{ display: 'inline-flex', gap: 4 }}>
                    <button className="btn btn-secondary" style={{ padding: '0.1rem 0.35rem' }}
                            title="Edit" onClick={() => setEditing({ ...r })}>
                      <Pencil size={12} />
                    </button>
                    <button className="btn btn-secondary" style={{ padding: '0.1rem 0.35rem' }}
                            title="Delete"
                            onClick={async () => {
                              try { setData(await deleteModelPricing(r.id)) }
                              catch (e) { toast.error(errText(e, 'Could not delete the row')) }
                            }}>
                      <Trash2 size={12} />
                    </button>
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>

        {editing ? (
          <div style={{
            display: 'grid', gap: '0.5rem', alignItems: 'end',
            gridTemplateColumns: '1fr 1.5fr 1fr 1fr auto auto',
            marginTop: '0.5rem', maxWidth: 760,
          }}>
            <Field label="Provider">
              <select value={editing.provider ?? ''}
                      onChange={(e) => setEditing((m) => ({ ...m, provider: e.target.value || null }))}>
                {PROVIDERS.map((p) => <option key={p} value={p}>{p || 'any provider'}</option>)}
              </select>
            </Field>
            <Field label="Model prefix">
              <input placeholder="e.g. claude-opus-5-5" value={editing.model_prefix ?? ''}
                     disabled={editing.id !== undefined}
                     onChange={(e) => setEditing((m) => ({ ...m, model_prefix: e.target.value }))} />
            </Field>
            <Field label="$ / 1M input">
              <input type="number" min={0} step="0.01" value={editing.input_usd_per_million ?? 0}
                     onChange={(e) => setEditing((m) => ({ ...m, input_usd_per_million: parseFloat(e.target.value) || 0 }))} />
            </Field>
            <Field label="$ / 1M output">
              <input type="number" min={0} step="0.01" value={editing.output_usd_per_million ?? 0}
                     onChange={(e) => setEditing((m) => ({ ...m, output_usd_per_million: parseFloat(e.target.value) || 0 }))} />
            </Field>
            <button className="btn btn-primary" onClick={saveModelRow}>Save</button>
            <button className="btn btn-secondary" title="Cancel" onClick={() => setEditing(null)}>
              <X size={14} />
            </button>
          </div>
        ) : (
          <button className="btn btn-secondary" style={{ marginTop: '0.5rem' }}
                  onClick={() => setEditing({ is_active: true, input_usd_per_million: 0, output_usd_per_million: 0 })}>
            <Plus size={14} /> Add model
          </button>
        )}
      </div>

      {/* ── Runtime seat defaults (money) ── */}
      <div>
        <div style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.25rem' }}>
          Runtime seat pricing defaults (money, not credits)
        </div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
          Charged to the APPLICATION's wallet when a runtime user is added — in the
          project owner's currency (India → INR, elsewhere → USD). A project can
          override these in its own Team panel.
        </div>
        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr', maxWidth: 560 }}>
          <MoneyField label="Per runtime user — USD" minor={runtime.default_runtime_user_cost_usd_minor}
                      onChange={(v) => setRuntime((r) => ({ ...r, default_runtime_user_cost_usd_minor: v }))} />
          <MoneyField label="Per runtime user — INR" minor={runtime.default_runtime_user_cost_inr_minor}
                      onChange={(v) => setRuntime((r) => ({ ...r, default_runtime_user_cost_inr_minor: v }))} />
          <MoneyField label="Per Pro-or-above invitee — USD" minor={runtime.default_pro_invite_cost_usd_minor}
                      hint="0 = free. A paying user brings their own plan."
                      onChange={(v) => setRuntime((r) => ({ ...r, default_pro_invite_cost_usd_minor: v }))} />
          <MoneyField label="Per Pro-or-above invitee — INR" minor={runtime.default_pro_invite_cost_inr_minor}
                      onChange={(v) => setRuntime((r) => ({ ...r, default_pro_invite_cost_inr_minor: v }))} />
        </div>
        <button className="btn btn-primary" style={{ marginTop: '0.75rem' }} onClick={saveSeats}
                disabled={savingSeats}>
          {savingSeats ? <Loader2 size={14} className="spin" /> : 'Save seat pricing'}
        </button>
      </div>

      <div>
        <div style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.5rem' }}>Currency</div>
        <InfoBox>
          Currency follows the user's country: <strong>India → INR</strong>,
          everywhere else <strong>USD</strong>. Plan and pack prices are set per
          currency on their own tabs.
        </InfoBox>
      </div>
    </div>
  )
}

/** A money input shown in major units, stored in minor units. */
function MoneyField({ label, minor, hint, onChange }: {
  label: string
  minor: number | undefined
  hint?: string
  onChange: (minor: number) => void
}) {
  return (
    <Field label={label} hint={hint}>
      <input
        type="number" min={0} step="0.01"
        value={minor !== undefined ? minor / 100 : 0}
        onChange={(e) => onChange(Math.round((parseFloat(e.target.value) || 0) * 100))}
        style={{ width: '100%' }}
      />
    </Field>
  )
}
